import type { PendingImage } from '../types/chat';

export const MAX_SOURCE_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_DATA_URL_LENGTH = 52_000;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

function imageToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Gagal membaca gambar.'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

export async function prepareImage(file: File): Promise<PendingImage> {
  if (!ALLOWED_IMAGE_TYPES.has(file.type) || file.size > MAX_SOURCE_IMAGE_BYTES) {
    throw new Error('Gunakan PNG, JPG, WebP, atau GIF dengan ukuran maksimal 5 MB.');
  }

  const source = await imageToDataUrl(file);
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('File gambar tidak valid.'));
    image.src = source;
  });

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Browser tidak mendukung pemrosesan gambar.');

  let longestEdge = Math.min(1280, Math.max(image.width, image.height));
  for (let attempt = 0; attempt < 6; attempt += 1) {
    const scale = Math.min(1, longestEdge / Math.max(image.width, image.height));
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    for (let quality = 0.82; quality >= 0.32; quality -= 0.1) {
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length <= MAX_IMAGE_DATA_URL_LENGTH) {
        return { dataUrl, name: file.name || 'gambar.jpg', size: file.size };
      }
    }
    longestEdge = Math.round(longestEdge * 0.65);
  }

  throw new Error('Gambar tidak dapat diperkecil dengan aman. Coba crop gambar terlebih dahulu.');
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
